(ns quuppa.peweb.tanstack-table
  (:require
    ["@tanstack/react-table" :as rt]
    ["@tanstack/react-virtual" :refer [useVirtualizer]]
    ["react" :as react]
    [reagent.core :as r]
    [quuppa.ui.icons :refer [icon]]))

(defn- safe-call-render-fn
  "Calls render-fn with appropriate number of arguments.
   Supports (fn [value]), (fn [value row-data]), and (fn [value row-data props]) signatures."
  [render-fn value row-data props]
  (let [arity (.-length render-fn)]
    (case arity
      1 (render-fn value)
      2 (render-fn value row-data)
      ;; Default to 3 args for compatibility with locators render functions
      (render-fn value row-data props))))

(defn- render-cell-value
  "Renders a cell value using the column config's render-fn if available.
   Returns [rendered-value cell-class] tuple."
  [value row-data {:keys [render-fn class-fn]} props]
  (let [rendered (if render-fn
                   (safe-call-render-fn render-fn value row-data props)
                   (if (some? value) (str value) "-"))
        cell-class (when class-fn (class-fn value row-data props))]
    [rendered cell-class]))

;; For now these values are hardcoded but in the future we can pass these from the outside in columns definition
(defn- build-columns [{:keys [all-columns selected-columns widths props]}]
  (clj->js
    (for [col-id selected-columns
          :let [col-key (if (keyword? col-id) col-id (keyword col-id))
                cfg (get all-columns col-key)
                w (or (get widths col-key) (:size cfg) (:width cfg) 120)
                value-fn-from-config (or (:value-fn cfg) #(get % col-key))]]
      #js {:id (name col-key)
           :accessorFn (fn [row]
                         (let [row-clj (js->clj row :keywordize-keys true)]
                           (value-fn-from-config row-clj)))
           :header (or (:header cfg) (:label cfg) (name col-id))
           :enableSorting (if (contains? cfg :enableSorting)
                            (:enableSorting cfg)
                            (get cfg :sortable true))
           :sortingFn (or (:sortingFn cfg) "auto")
           :enableResizing true
           :size w
           :minSize 70
           :maxSize 1000
           :meta #js {:config cfg :columnId (name col-key) :props props :value-fn value-fn-from-config}})))


(defn- table-header-cell [{:keys [^js header ^js table on-column-resize]}]
  (fn [{:keys [^js header ^js table on-column-resize]}]
    (let [column (.-column header)
          sorted? (.getIsSorted column)
          sort-handler (.getToggleSortingHandler column)
          size (.getSize column)
          is-resizing (.getIsResizing column)
          sortable? (.-enableSorting (.-columnDef column))]
      [:div.bg-dark.text-white.p-1.d-flex.flex-row.align-items-center.div-table-cell
       {:class (when sortable? "cursor-pointer")
        :style {:position "relative"
                :width (str size "px")
                :min-width "40px"
                :flex-shrink 0}
        :on-click (when sortable? sort-handler)}
       (when sortable?
         [icon (cond
                 (= sorted? "asc") :sort-asc
                 (= sorted? "desc") :sort-desc
                 :else :sort-asc)
          {:class ["mr-2"
                   "flex-shrink-0"
                   (when-not sorted? "text-muted")]}])
       [:span.text-truncate
        (rt/flexRender (.. header -column -columnDef -header)
                       (.getContext header))]
       [:div {:style {:position "absolute"
                      :right "0"
                      :top "0"
                      :height "100%"
                      :width "4px"
                      :background (if is-resizing "rgb(59, 130, 246)" "rgba(255, 255, 255, 0.3)")
                      :cursor "col-resize"
                      :user-select "none"
                      :touch-action "none"}
              :on-click (fn [e] (.stopPropagation e))
              :on-mouse-down (fn [e]
                               (.stopPropagation e)
                               ((.getResizeHandler header) e))
              :on-touch-start (fn [e]
                                (.stopPropagation e)
                                ((.getResizeHandler header) e))}]])))

(defn- virtualized-table-inner [^js props]
  (let [rows (.-data props)
        all-columns (.-allColumns props)
        selected-cols (js->clj (.-selectedColumns props))
        widths (js->clj (.-widths props) :keywordize-keys true)
        row-height (or (.-rowHeight props) 32)
        on-column-resize (.-onColumnResize props)
        on-row-click (.-onRowClick props)
        active-row-id (.-activeRowId props)
        extra-props (.-extraProps props)
        columns (build-columns {:all-columns all-columns
                                :selected-columns selected-cols
                                :widths widths
                                :props extra-props})

        [sorting set-sorting!] (react/useState #js [])
        [col-visibility set-col-visibility!] (react/useState #js {})
        [column-sizing set-column-sizing!] (react/useState
                                             (clj->js (or widths {})))
        table (rt/useReactTable
                #js {:data rows
                     :columns columns
                     :state #js {:sorting sorting
                                 :columnVisibility col-visibility
                                 :columnSizing column-sizing}
                     :onSortingChange set-sorting!
                     :onColumnVisibilityChange set-col-visibility!
                     :onColumnSizingChange (fn [updater]
                                             (let [new-sizing (if (fn? updater)
                                                                (updater column-sizing)
                                                                updater)]
                                               (set-column-sizing! new-sizing)
                                               (when on-column-resize
                                                 (doseq [[col-id size] (js->clj new-sizing)]
                                                   (on-column-resize col-id size)))))
                     :columnResizeMode "onChange"
                     :sortDescFirst false
                     :getCoreRowModel (rt/getCoreRowModel)
                     :getSortedRowModel (rt/getSortedRowModel)})

        table-container-ref (react/useRef)
        row-model (.getRowModel table)
        table-rows (.-rows row-model)
        row-virtualizer (useVirtualizer
                          #js {:count (.-length table-rows)
                               :getScrollElement (fn [] (.-current table-container-ref))
                               :estimateSize (fn [_] row-height)
                               :overscan 10})]
    (r/as-element
      [:div.d-flex.flex-column
       {:style {:width "100%"
                :flex "1 1 0"
                :min-height 0}}

       [:div
        {:ref table-container-ref
         :style {:flex "1 1 0"
                 :min-height 0
                 :overflow "auto"}}

        [:div.d-flex.flex-row.div-table-row.flex-shrink-0
         {:style {:position "sticky"
                  :top 0
                  :z-index 1
                  :min-width "fit-content"}}
         (for [hg (js/Array.from (.getHeaderGroups table))]
           (for [header (js/Array.from (.-headers hg))]
             ^{:key (.-id header)}
             [:f> table-header-cell {:header header
                                     :table table
                                     :on-column-resize on-column-resize}]))]

        [:div {:style {:height (str (.getTotalSize row-virtualizer) "px")
                       :width "100%"
                       :min-width "fit-content"
                       :position "relative"}}
         (doall
           (for [^js virtual-row (js/Array.from (.getVirtualItems row-virtualizer))
                 :let [row-index (.-index virtual-row)
                       row (aget table-rows row-index)
                       row-data (js->clj (.-original row) :keywordize-keys true)
                       row-id (:id row-data)
                       is-active? (and active-row-id (= active-row-id row-id))]]
             ^{:key (.-id row)}
             [:div.d-flex.flex-row.div-table-row
              {:style {:position "absolute"
                       :top (str (.-start virtual-row) "px")
                       :left 0
                       :width "100%"
                       :min-width "fit-content"
                       :height (str (.-size virtual-row) "px")}
               :class (str (when is-active? "div-table-row--active") (when on-row-click "cursor-pointer"))
               :on-click (when on-row-click
                           #(on-row-click row-data))}

              (doall
                (for [cell (js/Array.from (.getVisibleCells row))
                      :let [column (.-column cell)
                            ^js col-meta (.. column -columnDef -meta)
                            config (.-config col-meta)
                            cell-props (.-props col-meta)
                            value (.getValue cell)
                            [rendered cell-class] (render-cell-value value row-data config cell-props)]]
                  ^{:key (.-id cell)}
                  [:div.div-table-cell
                   {:class cell-class
                    :style {:width (str (.getSize column) "px")
                            :min-width "40px"
                            :flex-shrink 0}}
                   [:div.text-truncate
                    rendered]
                   [:div.cell-full-value
                    (when (and (some? rendered) (string? rendered)) rendered)]]))]))]]])))


(defn virtualized-table
  "Reusable virtualized table component with TanStack Table and react-virtual.

  Props:
  - :rows - vector of row data maps
  - :all-columns - map of all column definitions (supports :value-fn, :render-fn, :class-fn, :sortable, :label, :width)
  - :selected-columns - vector of column IDs to display
  - :widths - map of column ID to width in pixels
  - :table-id - unique identifier for the table
  - :row-height - height of each row (default 32)
  - :on-column-resize - callback fn [col-id new-width]
  - :on-row-click - callback fn [row-data]
  - :active-row-id - ID of the currently active/selected row
  - :props - extra props to pass to render functions (e.g., {:qpe-subscribe ... :qpe-dispatch ...})"
  [{:keys [rows all-columns selected-columns widths table-id
           row-height on-column-resize on-row-click
           active-row-id props]}]
  (react/createElement
    virtualized-table-inner
    #js {:data (clj->js rows)
         :allColumns all-columns
         :selectedColumns (clj->js selected-columns)
         :widths (clj->js widths)
         :tableId (when table-id (name table-id))
         :rowHeight row-height
         :activeRowId active-row-id
         :extraProps props
         :onColumnResize (when on-column-resize
                           (fn [col-id width]
                             (on-column-resize (keyword col-id) width)))
         :onRowClick (when on-row-click
                       (fn [row-data]
                         (on-row-click row-data)))}))
